// CompliancePage.js
import React from 'react';
import { useParams } from 'react-router-dom';
import styles from './CompliancePage.module.css';

// Compliance data for all 14 services
const complianceData = {
  'iso-27001': {
    title: 'ISO 27001 Certification Assistance',
    heroTitle: 'ISO 27001',
    description: 'ISO 27001 is an internationally recognized standard for managing information security. Virtual IP\'s ISO 27001 Certification Assistance service helps organizations implement the necessary processes and controls to achieve certification, ensuring that their Information Security Management System (ISMS) meets the required standards.',
    sections: [
      {
        title: 'ISMS Implementation Guidance',
        content: 'Virtual IP assists in the creation and implementation of an Information Security Management System (ISMS) that aligns with ISO 27001 standards. This includes setting up policies, controls, and procedures to protect the confidentiality, integrity, and availability of sensitive information.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Gap Analysis and Risk Assessment',
        content: 'We conduct a detailed gap analysis to assess your current security practices against the ISO 27001 requirements. This includes identifying areas where your organization is non-compliant and providing recommendations for improvement.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Certification Support',
        content: 'Our team supports you throughout the entire certification process, from documentation preparation to audits. We work closely with you to ensure all aspects of your ISMS are in line with ISO 27001, making the certification process smooth and efficient.',
        illustration: 'certification'
      },
      {
        title: 'Ongoing Compliance Monitoring',
        content: 'Achieving ISO 27001 certification is just the beginning. We offer continuous monitoring and auditing to ensure that your ISMS remains compliant with evolving standards and that your organization\'s security practices remain up-to-date.',
        illustration: 'monitoring'
      }
    ]
  },
  'soc2': {
    title: 'SOC 2 Compliance Support',
    heroTitle: 'SOC 2',
    description: 'SOC 2 is a critical standard for companies that handle sensitive customer data, particularly in the SaaS industry. Virtual IP’s SOC 2 Compliance Support service helps organizations achieve and maintain SOC 2 certification, which demonstrates the reliability and security of their systems.',
    sections: [
      {
        title: 'SOC 2 Readiness Assessment',
        content: 'We perform an initial readiness assessment to identify gaps between your current security posture and SOC 2 requirements. This includes reviewing your controls related to security, availability, processing integrity, confidentiality, and privacy.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Policy and Control Implementation',
        content: 'We assist in implementing the necessary policies, procedures, and controls required by SOC 2, ensuring that your organization meets the criteria for each of the Trust Services Criteria (TSC).',
        illustration: 'gap-analysis'
      },
      {
        title: 'Audit Preparation and Support',
        content: 'Our team provides comprehensive support during the SOC 2 audit process. We help prepare documentation, conduct internal audits, and ensure that your systems are fully compliant with SOC 2 requirements.',
        illustration: 'certification'
      },
      {
        title: 'Continuous SOC 2 Monitoring',
        content: 'SOC 2 compliance is an ongoing process. We provide continuous monitoring services to ensure that your organization maintains SOC 2 compliance year-round, assisting with annual audits and addressing any non-compliance issues that arise.',
        illustration: 'monitoring'
      }
    ]
  },
  'nist': {
    title: 'NIST Cybersecurity Framework Compliance',
    heroTitle: 'NIST CSF',
    description: 'The NIST Cybersecurity Framework provides a policy framework of computer security guidance for how private sector organizations can assess and improve their ability to prevent, detect, and respond to cyber attacks. Virtual IP helps organizations implement and maintain NIST CSF compliance.',
    sections: [
      {
        title: 'Framework Implementation',
        content: 'We guide organizations through implementing the five core functions of the NIST Cybersecurity Framework: Identify, Protect, Detect, Respond, and Recover. Our approach ensures comprehensive cybersecurity risk management.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Risk Assessment and Management',
        content: 'Our experts conduct thorough risk assessments to identify cybersecurity risks to organizational operations, assets, and individuals. We help develop risk management strategies aligned with NIST guidelines.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Security Controls Implementation',
        content: 'We assist in implementing appropriate security controls and safeguards to limit or contain the impact of potential cybersecurity events, ensuring your organization meets NIST standards.',
        illustration: 'certification'
      },
      {
        title: 'Continuous Improvement Program',
        content: 'NIST CSF emphasizes continuous improvement. We provide ongoing support to help organizations mature their cybersecurity practices and adapt to evolving threats and requirements.',
        illustration: 'monitoring'
      }
    ]
  },
  'cis': {
    title: 'CIS Controls (Center for Internet Security)',
    heroTitle: 'CIS Controls',
    description: 'The CIS Controls are a prioritized set of actions that collectively form a defense-in-depth set of best practices that mitigate the most common attack vectors. Virtual IP helps organizations implement and maintain CIS Controls for enhanced cybersecurity.',
    sections: [
      {
        title: 'Implementation Guidance',
        content: 'We provide comprehensive guidance on implementing the 18 CIS Controls, prioritizing the most critical security measures that provide the highest impact for your organization\'s security posture.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Security Assessment',
        content: 'Our team conducts detailed assessments against CIS Controls to identify gaps in your current security implementation and provide recommendations for improvement.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Control Implementation Support',
        content: 'We assist in implementing specific CIS Controls, from basic inventory and configuration management to advanced incident response and penetration testing capabilities.',
        illustration: 'certification'
      },
      {
        title: 'Ongoing Monitoring and Maintenance',
        content: 'CIS Controls require continuous monitoring and updating. We provide ongoing support to ensure your controls remain effective against evolving cyber threats.',
        illustration: 'monitoring'
      }
    ]
  },
  'hipaa': {
    title: 'HIPAA Compliance Services',
    heroTitle: 'HIPAA',
    description: 'The Health Insurance Portability and Accountability Act (HIPAA) establishes standards for protecting sensitive patient health information. Virtual IP\'s HIPAA Compliance Services help healthcare organizations protect PHI and meet regulatory requirements.',
    sections: [
      {
        title: 'Privacy and Security Rule Implementation',
        content: 'We help implement HIPAA Privacy and Security Rules, ensuring proper handling of Protected Health Information (PHI) and establishing required administrative, physical, and technical safeguards.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Risk Assessment and Analysis',
        content: 'Our experts conduct comprehensive risk assessments to identify vulnerabilities in your handling of PHI and provide recommendations for addressing identified risks.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Policy Development and Training',
        content: 'We assist in developing HIPAA-compliant policies and procedures, and provide training to ensure your workforce understands their responsibilities in protecting patient information.',
        illustration: 'certification'
      },
      {
        title: 'Breach Response and Incident Management',
        content: 'We provide guidance on breach notification requirements and help establish incident response procedures to minimize the impact of potential PHI breaches.',
        illustration: 'monitoring'
      }
    ]
  },
  'hitech': {
    title: 'HITECH (Health IT Compliance)',
    heroTitle: 'HITECH',
    description: 'The Health Information Technology for Economic and Clinical Health (HITECH) Act enhances HIPAA privacy and security protections. Virtual IP helps organizations comply with HITECH requirements for health information technology.',
    sections: [
      {
        title: 'Enhanced Security Requirements',
        content: 'We help implement enhanced security measures required by HITECH, including encryption, access controls, and audit logging for electronic health information systems.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Breach Notification Compliance',
        content: 'Our team assists in establishing procedures for HITECH breach notification requirements, including notification to patients, HHS, and media when required.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Business Associate Agreements',
        content: 'We help develop and manage Business Associate Agreements (BAAs) that meet HITECH requirements for third-party vendors handling protected health information.',
        illustration: 'certification'
      },
      {
        title: 'Audit and Enforcement Preparation',
        content: 'HITECH strengthened enforcement of HIPAA. We help organizations prepare for potential audits and ensure compliance with enhanced penalty structures.',
        illustration: 'monitoring'
      }
    ]
  },
  'gdpr': {
    title: 'GDPR Compliance Services',
    heroTitle: 'GDPR',
    description: 'The General Data Protection Regulation (GDPR) is a comprehensive data protection law that affects any organization processing EU residents\' personal data. Virtual IP\'s GDPR Compliance Services help organizations meet these stringent requirements.',
    sections: [
      {
        title: 'Data Protection Framework',
        content: 'We help establish comprehensive data protection frameworks that comply with GDPR principles, including lawfulness, fairness, transparency, and data minimization.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Privacy Impact Assessments',
        content: 'Our experts conduct Data Protection Impact Assessments (DPIAs) to identify and mitigate privacy risks in your data processing activities.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Rights Management and Procedures',
        content: 'We assist in implementing procedures to handle data subject rights, including access, rectification, erasure, and portability requests under GDPR.',
        illustration: 'certification'
      },
      {
        title: 'Breach Response and Notification',
        content: 'GDPR requires breach notification within 72 hours. We help establish incident response procedures to meet these strict notification requirements.',
        illustration: 'monitoring'
      }
    ]
  },
  'pci-dss': {
    title: 'PCI-DSS Compliance Services',
    heroTitle: 'PCI-DSS',
    description: 'The Payment Card Industry Data Security Standard (PCI-DSS) is a set of security standards designed to ensure that all companies that accept, process, store or transmit credit card information maintain a secure environment.',
    sections: [
      {
        title: 'Security Requirements Implementation',
        content: 'We help implement the 12 PCI-DSS requirements, including building and maintaining secure networks, protecting stored cardholder data, and maintaining vulnerability management programs.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Network Security and Access Controls',
        content: 'Our team assists in implementing strong access control measures, network segmentation, and encryption to protect cardholder data environments.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Compliance Validation and Testing',
        content: 'We provide support for PCI-DSS compliance validation, including Self Assessment Questionnaires (SAQs) and preparation for qualified security assessor audits.',
        illustration: 'certification'
      },
      {
        title: 'Ongoing Monitoring and Maintenance',
        content: 'PCI-DSS requires continuous monitoring. We help maintain compliance through regular security testing, vulnerability scanning, and policy updates.',
        illustration: 'monitoring'
      }
    ]
  },
  'fisma': {
    title: 'FISMA (Federal Information Security)',
    heroTitle: 'FISMA',
    description: 'The Federal Information Security Management Act (FISMA) provides a comprehensive framework for ensuring the effectiveness of information security controls over federal information resources.',
    sections: [
      {
        title: 'Security Control Implementation',
        content: 'We help implement NIST SP 800-53 security controls required for FISMA compliance, ensuring appropriate protection for federal information systems.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Authorization Process Support',
        content: 'Our team provides support throughout the FISMA authorization process, including system categorization, security control assessment, and authorization documentation.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Continuous Monitoring Program',
        content: 'We assist in establishing continuous monitoring programs required by FISMA to maintain security authorization and ensure ongoing compliance.',
        illustration: 'certification'
      },
      {
        title: 'Risk Management Framework',
        content: 'FISMA requires implementation of the Risk Management Framework (RMF). We guide organizations through all six RMF steps for comprehensive security management.',
        illustration: 'monitoring'
      }
    ]
  },
  'cmmc': {
    title: 'CMMC (Cybersecurity Maturity Model Certification) Support',
    heroTitle: 'CMMC',
    description: 'The Cybersecurity Maturity Model Certification (CMMC) is a unifying standard for the implementation of cybersecurity across the Defense Industrial Base (DIB). Virtual IP helps contractors achieve required CMMC certification levels.',
    sections: [
      {
        title: 'Maturity Level Assessment',
        content: 'We assess your organization\'s current cybersecurity maturity and help determine the appropriate CMMC level required for your DoD contracts.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Control Implementation Guidance',
        content: 'Our experts guide implementation of required security controls across the five CMMC maturity levels, ensuring your organization meets certification requirements.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Certification Preparation',
        content: 'We provide comprehensive support to prepare for CMMC assessment, including documentation preparation, process implementation, and pre-assessment reviews.',
        illustration: 'certification'
      },
      {
        title: 'Ongoing Compliance Management',
        content: 'CMMC requires ongoing compliance monitoring. We help maintain your certification through continuous improvement and regular compliance assessments.',
        illustration: 'monitoring'
      }
    ]
  },
  'fedramp': {
    title: 'FedRAMP Compliance',
    heroTitle: 'FedRAMP',
    description: 'The Federal Risk and Authorization Management Program (FedRAMP) provides a standardized approach to security assessment, authorization, and continuous monitoring for cloud products and services.',
    sections: [
      {
        title: 'Authorization Process Support',
        content: 'We guide cloud service providers through the FedRAMP authorization process, including JAB P-ATO and Agency ATO paths.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Security Control Implementation',
        content: 'Our team helps implement FedRAMP security controls based on NIST SP 800-53, ensuring your cloud services meet federal security requirements.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Documentation and Assessment',
        content: 'We assist in preparing required FedRAMP documentation, including System Security Plans (SSP) and supporting assessment artifacts.',
        illustration: 'certification'
      },
      {
        title: 'Continuous Monitoring Program',
        content: 'FedRAMP requires robust continuous monitoring. We help establish monitoring programs to maintain authorization and ensure ongoing compliance.',
        illustration: 'monitoring'
      }
    ]
  },
  'itar': {
    title: 'ITAR (International Traffic in Arms Regulations) Compliance',
    heroTitle: 'ITAR',
    description: 'The International Traffic in Arms Regulations (ITAR) controls the import and export of defense-related articles and services. Virtual IP helps organizations comply with ITAR cybersecurity requirements.',
    sections: [
      {
        title: 'Defense Article Protection',
        content: 'We help implement security measures to protect ITAR-controlled technical data and defense articles from unauthorized access or disclosure.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Access Control and Screening',
        content: 'Our team assists in establishing proper access controls and personnel screening procedures required for handling ITAR-controlled information.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Export Control Compliance',
        content: 'We provide guidance on ITAR export control requirements, including proper licensing, registration, and reporting obligations.',
        illustration: 'certification'
      },
      {
        title: 'Audit and Compliance Monitoring',
        content: 'ITAR compliance requires ongoing monitoring. We help establish audit programs and compliance monitoring to ensure continued adherence to regulations.',
        illustration: 'monitoring'
      }
    ]
  },
  'iso-42001': {
    title: 'ISO/IEC 42001 (AI Management System)',
    heroTitle: 'ISO 42001',
    description: 'ISO/IEC 42001 provides requirements for establishing, implementing, maintaining and continually improving an Artificial Intelligence Management System (AIMS). Virtual IP helps organizations responsibly develop and use AI systems.',
    sections: [
      {
        title: 'AI Management System Implementation',
        content: 'We guide organizations in establishing comprehensive AI management systems that ensure responsible AI development, deployment, and operation.',
        illustration: 'iso-diagram'
      },
      {
        title: 'AI Risk Assessment and Management',
        content: 'Our team conducts thorough AI risk assessments to identify potential ethical, legal, and operational risks associated with AI system implementation.',
        illustration: 'gap-analysis'
      },
      {
        title: 'AI Governance and Ethics',
        content: 'We help establish AI governance frameworks that ensure ethical AI practices, transparency, and accountability in AI system development and deployment.',
        illustration: 'certification'
      },
      {
        title: 'Continuous AI System Monitoring',
        content: 'AI systems require ongoing monitoring for performance, bias, and compliance. We help establish monitoring programs for continuous AI system improvement.',
        illustration: 'monitoring'
      }
    ]
  },
  'cobit': {
    title: 'COBIT (IT Governance Framework)',
    heroTitle: 'COBIT',
    description: 'COBIT (Control Objectives for Information and Related Technologies) is a framework for IT management and governance. Virtual IP helps organizations implement COBIT to align IT with business objectives and ensure effective IT governance.',
    sections: [
      {
        title: 'IT Governance Framework Implementation',
        content: 'We help implement COBIT\'s comprehensive IT governance framework, ensuring IT activities align with business objectives and deliver value to the organization.',
        illustration: 'iso-diagram'
      },
      {
        title: 'Process Maturity Assessment',
        content: 'Our experts assess your organization\'s IT process maturity using COBIT\'s maturity models and provide roadmaps for improvement.',
        illustration: 'gap-analysis'
      },
      {
        title: 'Control Objectives Implementation',
        content: 'We assist in implementing COBIT control objectives across IT processes, ensuring proper governance, risk management, and compliance.',
        illustration: 'certification'
      },
      {
        title: 'Performance Monitoring and Improvement',
        content: 'COBIT emphasizes continuous improvement. We help establish KPIs and monitoring mechanisms to track IT governance effectiveness and drive improvement.',
        illustration: 'monitoring'
      }
    ]
  }
};

const CompliancePage = () => {
  const { complianceType } = useParams();
  const pathSegments = window.location.pathname.split('/');
  const actualComplianceType = pathSegments[pathSegments.length - 1];
  const data = complianceData[actualComplianceType];

  if (!data) {
    return (
      <div style={{ padding: '100px 20px', textAlign: 'center' }}>
        <h1>Compliance service not found</h1>
        <p>The requested compliance service does not exist.</p>
      </div>
    );
  }

  return (
    <div className={styles.compliancePage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <div className={styles.heroPattern}></div>
          <div className={styles.heroElements}>
            <div className={styles.heroIcon}>
              <div className={styles.iconCircle}>
                <div className={styles.iconCheckmark}>✓</div>
              </div>
              <div className={styles.iconRings}>
                <div className={styles.ring1}></div>
                <div className={styles.ring2}></div>
                <div className={styles.ring3}></div>
              </div>
            </div>
          </div>
          <div className={styles.heroBigText}>{data.heroTitle}</div>
        </div>
        <div className={styles.heroContainer}>
          <h1 className={styles.heroTitle}>{data.title}</h1>
        </div>
      </section>

      {/* Description Section */}
      <section className={styles.descriptionSection}>
        <div className={styles.container}>
          <p className={styles.description}>{data.description}</p>
        </div>
      </section>

      {/* Sections */}
      {data.sections.map((section, index) => (
        <section
          key={index}
          className={`${styles.contentSection} ${
            index % 2 === 1 ? styles.alternateSection : ''
          }`}
        >
          <div className={styles.container}>
            <div className={styles.sectionGrid}>
              <div className={styles.sectionContent}>
                <h2 className={styles.sectionTitle}>{section.title}</h2>
                <div className={styles.titleUnderline}></div>
                <p className={styles.sectionDescription}>{section.content}</p>
              </div>
              <div className={styles.sectionVisual}>
                <div className={`${styles.illustration} ${styles[section.illustration]}`}>
                  {section.illustration === 'iso-diagram' && (
                    <div className={styles.isoDiagram}>
                      <div className={styles.centralHub}>
                        <span>ISO<br/>{data.heroTitle}</span>
                      </div>
                      <div className={styles.connectedNodes}>
                        <div className={styles.node} data-position="top">
                          <div className={styles.nodeIcon}>⚡</div>
                        </div>
                        <div className={styles.node} data-position="top-right">
                          <div className={styles.nodeIcon}>🛡️</div>
                        </div>
                        <div className={styles.node} data-position="right">
                          <div className={styles.nodeIcon}>📊</div>
                        </div>
                        <div className={styles.node} data-position="bottom-right">
                          <div className={styles.nodeIcon}>🔒</div>
                        </div>
                        <div className={styles.node} data-position="bottom">
                          <div className={styles.nodeIcon}>📈</div>
                        </div>
                        <div className={styles.node} data-position="bottom-left">
                          <div className={styles.nodeIcon}>🎯</div>
                        </div>
                        <div className={styles.node} data-position="left">
                          <div className={styles.nodeIcon}>⚙️</div>
                        </div>
                        <div className={styles.node} data-position="top-left">
                          <div className={styles.nodeIcon}>🔍</div>
                        </div>
                      </div>
                      <div className={styles.connectionLines}>
                        <div className={styles.connectionLine}></div>
                        <div className={styles.connectionLine}></div>
                        <div className={styles.connectionLine}></div>
                        <div className={styles.connectionLine}></div>
                        <div className={styles.connectionLine}></div>
                        <div className={styles.connectionLine}></div>
                        <div className={styles.connectionLine}></div>
                        <div className={styles.connectionLine}></div>
                      </div>
                    </div>
                  )}

                  {section.illustration === 'gap-analysis' && (
                    <div className={styles.gapAnalysis}>
                      <div className={styles.analysisCircle}>
                        <div className={styles.magnifyingGlass}>
                          <div className={styles.glassCircle}></div>
                          <div className={styles.glassHandle}></div>
                        </div>
                        <div className={styles.analysisIcons}>
                          <div className={styles.analysisIcon}>📋</div>
                          <div className={styles.analysisIcon}>📊</div>
                          <div className={styles.analysisIcon}>🎯</div>
                          <div className={styles.analysisIcon}>⚠️</div>
                          <div className={styles.analysisIcon}>📈</div>
                          <div className={styles.analysisIcon}>🔍</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {section.illustration === 'certification' && (
                    <div className={styles.certification}>
                      <div className={styles.certificationTablet}>
                        <div className={styles.tabletBase}></div>
                        <div className={styles.tabletScreen}>
                          <div className={styles.graduationCap}></div>
                          <div className={styles.certificate}>
                            <div className={styles.certificateIcon}>✓</div>
                          </div>
                          <div className={styles.ribbonBadge}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {section.illustration === 'monitoring' && (
                    <div className={styles.monitoring}>
                      <div className={styles.monitoringPlatform}>
                        <div className={styles.platformBase}></div>
                        <div className={styles.monitoringPerson}>
                          <div className={styles.personBody}></div>
                          <div className={styles.personHead}></div>
                        </div>
                        <div className={styles.monitoringScreen}>
                          <div className={styles.screenFrame}></div>
                          <div className={styles.screenDisplay}>
                            <div className={styles.cycleArrows}>
                              <div className={styles.arrow1}>↻</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};

export default CompliancePage;